import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Button,
    LinearProgress,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Paper,
} from "@mui/material";
import {
    Search,
    BookOpen,
    Clock,
    Star,
    Users,
    Layout,
    Filter,
    ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const TutoringCourses = () => {
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const navigate = useNavigate();

    const courses = [
        {
            id: 1,
            title: "Advanced Algebra & Calculus",
            category: "Mathematics",
            lessons: 24,
            duration: "12h 30m",
            rating: 4.8,
            students: 1205,
            progress: 75,
            image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=600",
            status: "In Progress",
        },
        {
            id: 2,
            title: "Quantum Physics Fundamentals",
            category: "Physics",
            lessons: 18,
            duration: "10h 15m",
            rating: 4.9,
            students: 840,
            progress: 40,
            image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&q=80&w=600",
            status: "In Progress",
        },
        {
            id: 3,
            title: "organic Chemistry Deep Dive",
            category: "Chemistry",
            lessons: 32,
            duration: "15h 45m",
            rating: 4.7,
            students: 650,
            progress: 0,
            image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=600",
            status: "Recently Added",
        },
        {
            id: 4,
            title: "Modern World History",
            category: "History",
            lessons: 20,
            duration: "14h 20m",
            rating: 4.6,
            students: 1100,
            progress: 100,
            image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?auto=format&fit=crop&q=80&w=600",
            status: "Completed",
        },
    ];

    const filteredCourses = courses.filter(course => {
        const matchesSearch = course.title.toLowerCase().includes(search.toLowerCase());
        const matchesFilter = filter === "all" || course.status.toLowerCase().replace(" ", "") === filter;
        return matchesSearch && matchesFilter;
    });

    return (
        <Box sx={{ p: 4, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            {/* Header Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1e293b", mb: 2 }}>
                    My Learning Catalog
                </Typography>
                <Typography variant="body1" sx={{ color: "#64748b", mb: 4 }}>
                    Expand your knowledge with our premium courses designed for excellence.
                </Typography>

                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            placeholder="Search courses, topics, or tutors..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={20} color="#94a3b8" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: "16px",
                                    bgcolor: "#fff",
                                    "& fieldset": { borderColor: "rgba(0,0,0,0.05)" },
                                    "&:hover fieldset": { borderColor: "#4f46e5" },
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <Select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                sx={{
                                    borderRadius: "16px",
                                    bgcolor: "#fff",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.05)" },
                                }}
                            >
                                <MenuItem value="all">All Courses</MenuItem>
                                <MenuItem value="inprogress">In Progress</MenuItem>
                                <MenuItem value="recentlyadded">Recently Added</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            {/* Stats Quickbar */}
            <Box sx={{ mb: 6, display: "flex", gap: 3, flexWrap: "wrap" }}>
                {[
                    { label: "Active Courses", value: "2", color: "#4f46e5", icon: <Layout size={18} /> },
                    { label: "Completed", value: "1", color: "#10b981", icon: <CheckCircle size={18} /> },
                    { label: "Total Hours", value: "48h", color: "#06b6d4", icon: <Clock size={18} /> },
                ].map((stat, idx) => (
                    <Paper key={idx} sx={{
                        px: 3, py: 1.5,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        border: "1px solid rgba(0,0,0,0.05)",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                    }}>
                        <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748b" }}>{stat.label}:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "#1e293b" }}>{stat.value}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* Courses Grid */}
            <Grid container spacing={4}>
                {filteredCourses.map((course) => (
                    <Grid item xs={12} md={6} lg={4} key={course.id}>
                        <motion.div
                            layout
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Card sx={{
                                borderRadius: "28px",
                                overflow: "hidden",
                                border: "1px solid rgba(0,0,0,0.05)",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    transform: "translateY(-8px)",
                                    boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                                }
                            }}>
                                <Box sx={{ position: "relative" }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={course.image}
                                        alt={course.title}
                                    />
                                    <Chip
                                        label={course.category}
                                        sx={{
                                            position: "absolute",
                                            top: 16,
                                            left: 16,
                                            bgcolor: "rgba(255,255,255,0.9)",
                                            backdropFilter: "blur(8px)",
                                            fontWeight: 700,
                                            color: "#4f46e5",
                                            px: 1
                                        }}
                                    />
                                    {course.progress > 0 && (
                                        <Box sx={{
                                            position: "absolute",
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            p: 2,
                                            background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                                            color: "#fff"
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: "block" }}>
                                                {course.progress === 100 ? "Completed" : `${course.progress}% Explored`}
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={course.progress}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    bgcolor: "rgba(255,255,255,0.3)",
                                                    "& .MuiLinearProgress-bar": { bgcolor: "#10b981" }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Star size={16} color="#f59e0b" fill="#f59e0b" />
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>{course.rating}</Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748b" }}>
                                            <Users size={16} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{course.students}</Typography>
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 2, height: "3.2rem", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                        {course.title}
                                    </Typography>

                                    <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                                            <BookOpen size={16} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{course.lessons} Lessons</Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                                            <Clock size={16} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{course.duration}</Typography>
                                        </Box>
                                    </Box>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        endIcon={<ArrowRight size={18} />}
                                        onClick={() => navigate("/online-tutoring/courses/detail")}
                                        sx={{
                                            borderRadius: "16px",
                                            py: 1.5,
                                            textTransform: "none",
                                            fontWeight: 700,
                                            bgcolor: course.progress === 100 ? "#10b981" : "#4f46e5",
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: course.progress === 100 ? "#059669" : "#4338ca",
                                                boxShadow: "0 8px 16px rgba(79, 70, 229, 0.25)"
                                            }
                                        }}
                                    >
                                        {course.progress === 100 ? "Review Material" : (course.progress > 0 ? "Continue Course" : "Start Learning")}
                                    </Button>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default TutoringCourses;

const CheckCircle = ({ size, color }: { size: number, color?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);
